import * as React from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { FLAGS } from '@atbash-labs/ontology-flags'
import { resolvePlantTab, type PlantTab } from '@atbash-labs/plantview-links'
import { PlantPaintProvider, usePlantSessionPaint } from '@axilon-apps/domains-ontology-ui-state'
import { useFlag } from '../../components/flags/useFlag'
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { useDebugMode } from '../../lib/debug-mode'
import { PlantChangesBar } from './PlantChangesBar'
import { paintedPlantTabs } from './plant-tab-paint'
import { plantTabsForDebug, type PlantTabSpec } from './plant-tabs'
import { SessionTouchedDot } from './session-mark-ui'
import { usePlantObcTheme } from './use-plant-theme'

/**
 * Parent wrapper for the Plant Viewer. Renders the Equipment / Processes /
 * Areas / Compute / PLC / DCS tab bar and hosts the OpenBridge CSS theme scope.
 * Each tab is a nested route; switching tabs navigates between them so
 * deep links keep working. Session paint filter state lives in
 * `PlantPaintProvider` so it persists across tabs and drill-downs.
 */
const PlantPage = (): React.ReactElement => {
  const theme = usePlantObcTheme()
  const debugMode = useDebugMode()
  const dcs = useFlag(FLAGS.UI_PLANT_DCS)
  const network = useFlag(FLAGS.UI_PLANT_NETWORK)
  const safety = useFlag(FLAGS.UI_PLANT_SAFETY)
  const navigate = useNavigate()
  const location = useLocation()
  const activeTab = resolvePlantTab(location.pathname)
  const tabs = React.useMemo(
    () => plantTabsForDebug(debugMode, { dcs, network, safety }),
    [debugMode, dcs, network, safety]
  )

  const onTabChange = React.useCallback(
    (next: string) => {
      const tab = tabs.find((t) => t.value === next)
      if (!tab || tab.value === activeTab) return
      navigate(tab.path)
    },
    [activeTab, navigate, tabs]
  )

  return (
    // Joy `<Box sx={{ display:'flex', flexDirection:'column', minHeight:
    // 'calc(100vh - 32px)', gap: 2 }}>` (Joy gap unit 2 = 16 px) →
    // `flex flex-col gap-4 min-h-[calc(100vh-2rem)]`.
    <div data-obc-theme={theme} className="flex min-h-[calc(100vh-2rem)] flex-col gap-4">
      <PlantPaintProvider>
        <div className="flex flex-col gap-1.5">
          <PlantChangesBar />
          <PlantTabBar tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
        </div>
        <Outlet />
      </PlantPaintProvider>
    </div>
  )
}

function PlantTabBar({
  tabs,
  activeTab,
  onTabChange
}: {
  tabs: PlantTabSpec[]
  activeTab: PlantTab
  onTabChange: (next: string) => void
}): React.ReactElement {
  const { markById, markFor } = usePlantSessionPaint()
  const painted = React.useMemo(() => paintedPlantTabs(markById, markFor), [markById, markFor])

  return (
    <Tabs value={activeTab} onValueChange={onTabChange}>
      <TabsList variant="line" aria-label="Plant sections">
        {tabs.map((t) => {
          const Icon = t.icon
          const hasPaint = painted.has(t.value)
          return (
            <TabsTrigger key={t.value} value={t.value}>
              <Icon size={16} />
              {t.label}
              {hasPaint ? <SessionTouchedDot /> : null}
            </TabsTrigger>
          )
        })}
      </TabsList>
    </Tabs>
  )
}

export default PlantPage
